import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { ensureUser, getSubCategoryLabel } from '../../util/data';
import { AvatarLarge, IconCollection, IconSpinner } from '../../components';
import StarRatings from '../../components/StarRatings/StarRatings';
import ImageCarousel from '../ListingPage/ImageCarousel/ImageCarousel';
import SectionMapMaybe from '../ListingPage/SectionMapMaybe';
import { DesktopReviews, MobileReviews } from './ProfilePage';
import Slider from 'react-slick';

import css from './CompanyProfilePage.module.css';

const GalleryPrevArrow = props => {
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      aria-label="Previous"
      className={classNames(css.galleryArrow, css.galleryArrowPrev, className)}
      style={style}
      onClick={onClick}
    >
      <span className={css.galleryArrowIcon} aria-hidden="true">
        ‹
      </span>
    </button>
  );
};

const GalleryNextArrow = props => {
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      aria-label="Next"
      className={classNames(css.galleryArrow, css.galleryArrowNext, className)}
      style={style}
      onClick={onClick}
    >
      <span className={css.galleryArrowIcon} aria-hidden="true">
        ›
      </span>
    </button>
  );
};

const SECTIONS = [
  { id: 'about', label: 'About' },
  { id: 'services', label: 'Services' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'location', label: 'Location' },
  { id: 'faqs', label: 'FAQs' },
];

const settings = {
  dots: false,
  infinite: false,
  speed: 500,
  mobileFirst: true,
  slidesToShow: 3.4,
  slidesToScroll: 3.4,

  centerPadding: '34px',
  arrows: true,
  prevArrow: <GalleryPrevArrow />,
  nextArrow: <GalleryNextArrow />,

  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
        centerPadding: '0',
        arrows: true,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1.4,
        slidesToScroll: 1.4,

        centerPadding: '0px',
        arrows: false,
      },
    },
  ],
};

// Define CompanyProfilePage component
const CompanyProfilePage = props => {
  const config = useConfiguration();
  const hasUser = !!props.user;
  const profileUser = hasUser ? ensureUser(props.user) : null;
  const profileAttrs = profileUser?.attributes?.profile || {};
  const { displayName, bio } = profileAttrs;
  const publicData = profileAttrs.publicData || {};

  const {
    companyName,
    companyCoverPhoto,
    companyGalleryPhotos = [],
    yearFounded,
    instagram,
    facebook,
    website,
    youtube,
    yelp,
    address1,
    offeredServices = [],
    question1,
    question2,
    question3,
  } = publicData;

  const bannerUrl = companyCoverPhoto || companyGalleryPhotos[0] || null;
  const transformedCategories = config?.categoryConfiguration?.categories?.map(cat => ({
    label: cat.name,
    value: cat.id,
    subCategories: cat.subcategories.map(sub => ({
        label: sub.name,
        value: sub.id
    }))
  }));

  const categories = transformedCategories || [];

  const selectedPlace = address1 && address1.selectedPlace;
  const city = selectedPlace && selectedPlace.city;
  const region = selectedPlace && selectedPlace.region;
  const cityState = [city, region].filter(Boolean).join(', ');

  const origin = selectedPlace && selectedPlace.origin;
  const geolocation = origin ? { lat: origin.lat, lng: origin.lng } : null;
  const addressLine = selectedPlace && selectedPlace.address;

  const mapsConfig = config.maps || {};

  const yearsInBusiness = useMemo(() => {
    if (!yearFounded) return null;
    const year = parseInt(yearFounded, 10);
    const currentYear = new Date().getFullYear();
    if (!year || Number.isNaN(year) || year > currentYear) return null;
    const diff = currentYear - year;
    return diff > 0 ? diff : null;
  }, [yearFounded]);

  const [activeSection, setActiveSection] = useState('about');
  const sectionRefs = useRef({});

  useEffect(() => {
    const handleScroll = () => {
      const targetOffset = 100; // approximate topbar + nav height

      let bestIdAhead = null;
      let bestDelta = Number.POSITIVE_INFINITY;
      let lastAboveId = null;

      SECTIONS.forEach(sec => {
        const el = sectionRefs.current[sec.id];
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const delta = rect.top - targetOffset;

        if (delta >= 0 && delta < bestDelta) {
          bestDelta = delta;
          bestIdAhead = sec.id;
        }
        if (rect.top < targetOffset) {
          lastAboveId = sec.id;
        }
      });

      const nextActive = bestIdAhead || lastAboveId || SECTIONS[0].id;
      setActiveSection(nextActive);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = id => {
    const el = sectionRefs.current[id];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const target = window.scrollY + rect.top - 100; // offset for sticky nav
    window.scrollTo({ top: target, behavior: 'smooth' });
    setActiveSection(id);
  };

  const fullName = companyName || displayName || '';
  const bioText = bio || '';
  const shouldClampBio = bioText.length > 50;
  const [bioExpanded, setBioExpanded] = useState(false);
  const visibleBio =
    !shouldClampBio || bioExpanded ? bioText : `${bioText.slice(0, 50)}...`;

  const [openFaqs, setOpenFaqs] = useState({});
  const [expandedServices, setExpandedServices] = useState({});

  const toggleServiceDesc = key => {
    setExpandedServices(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isMobileLayout = props.viewport && props.viewport.width < 768;

  const averageRating =
    typeof props.averageRating === 'number' ? props.averageRating : 0;
  const transactionCount =
    typeof props.transactionCount === 'number' ? props.transactionCount : 0;
  const roundedRating = Math.round(averageRating || 0);

  const galleryImages = useMemo(
    () =>
      (companyGalleryPhotos || []).map(url => ({
        type: 'image',
        attributes: {
          variants: {
            'landscape-crop': { url, width: 1200, height: 800 },
            'landscape-crop2x': { url, width: 2400, height: 1600 },
          },
        },
      })),
    [companyGalleryPhotos]
  );

  const hasServices =
    Array.isArray(offeredServices) && offeredServices.length > 0;
  const hasGallery =
    Array.isArray(companyGalleryPhotos) && companyGalleryPhotos.length > 0;

  // Build FAQ list from dynamic array or legacy fields
  const faqsList = useMemo(() => {
    const arr = Array.isArray(publicData?.faqs) ? publicData.faqs : [];
    if (arr.length > 0) {
      // return arr
      //   .map(it => ({
      //     label: (it && typeof it.label === 'string' ? it.label : '') || '',
      //     answer: (it && typeof it.answer === 'string' ? it.answer : '') || '',
      //   }))
      //   .filter(it => it.label || it.answer);
      return arr.filter(it => it && it.label && it.answer);
    }
    const legacy = [question1, question2, question3]
      .filter(Boolean)
      .map((ans, i) => ({ label: `Question ${i + 1}`, answer: ans }));
    return legacy;
  }, [publicData, question1, question2, question3]);

  const hasFaqs = faqsList.length > 0;
  const hasLocation = !!addressLine || !!geolocation;

  if (!props.user) {
    return (
      <div className={css.loadingSpinner}>
        <IconSpinner />
      </div>
    );
  }

  // If there is no user at all, render a simple fallback instead of trying to access profile data
  if (!hasUser) {
    return (
      <div className={css.pageRoot}>
        <div className={css.section}>
          <h1 className={css.sectionTitle}>Company profile not available</h1>
          <p>We couldn't find a company profile for this user yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={css.pageRoot}>
      <nav className={css.stickyNav}>
        <div className={css.navInner}>
          {SECTIONS.map(sec => (
              sec.id === 'reviews' && (!Array.isArray(props?.reviews) || props?.reviews.length === 0)) ? null : (
              <button
                key={sec.id}
                type="button"
                className={classNames(
                  css.navItem,
                  activeSection === sec.id ? css.navItemActive : null
                )}
                onClick={() => handleNavClick(sec.id)}
              >
                {sec.label}
              </button>
          ))}
        </div>
      </nav>

      {/* About Section */}
      <section
        id="about"
        ref={el => {
          sectionRefs.current.about = el;
        }}
        className={css.section}
      >
        {bannerUrl ? (
          <div className={css.bannerWrapper}>
            <img
              src={bannerUrl}
              alt={fullName || 'Company banner'}
              className={css.bannerImage}
              loading="lazy"
            />
          </div>
        ) : null}

        <div className={css.aboutCard}>
          <div className={css.aboutLeft}>
            <AvatarLarge
              className={css.aboutAvatar}
              user={profileUser}
              disableProfileLink
            />
          </div>

          <div className={css.aboutMiddle}>
            <div className={css.companyNameRow}>
              <h1 className={css.companyName}>{fullName}</h1>
              <div className={css.aboutRight}>
                <div className={css.socialRow}>
                  {yelp ? (
                    <a
                      href={yelp}
                      className={css.socialLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconCollection icon="yelp_logo" />
                    </a>
                  ) : null}
                  {website ? (
                    <a
                      href={website}
                      className={css.socialLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconCollection icon="website_logo" />
                    </a>
                  ) : null}
                  {youtube ? (
                    <a
                      href={youtube}
                      className={css.socialLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconCollection icon="youtube_logo" />
                    </a>
                  ) : null}
                  {instagram ? (
                    <a
                      href={instagram}
                      className={css.socialLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconCollection icon="instagram_logo" />
                    </a>
                  ) : null}
                  {facebook ? (
                    <a
                      href={facebook}
                      className={css.socialLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconCollection icon="facebook_logo" />
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
            <div className={css.transactionCount}>
              <StarRatings rating={roundedRating || 0} /> {transactionCount}{' '}
              Transactions
            </div>
            <div className={css.metaRow}>
              {cityState ? (
                <span className={css.metaPrimary}>{cityState}</span>
              ) : null}
              {cityState && yearsInBusiness ? (
                <span className={css.metaDot}>•</span>
              ) : null}
              {yearsInBusiness ? (
                <span className={css.metaSecondary}>
                  {yearsInBusiness}+ years in business
                </span>
              ) : null}
            </div>

            {bioText ? (
              <p className={css.bioText}>
                {visibleBio}
                {shouldClampBio ? (
                  <button
                    type="button"
                    className={css.readMoreBtn}
                    onClick={() => setBioExpanded(v => !v)}
                  >
                    {bioExpanded ? ' READ LESS' : ' READ MORE...'}
                  </button>
                ) : null}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section
        id="services"
        ref={el => {
          sectionRefs.current.services = el;
        }}
        className={classNames(css.section, css.servicesSection)}
      >
        <h2 className={css.sectionTitle}>Our Services</h2>
        {hasServices ? (
          <div className={css.servicesGrid}>
            {(offeredServices || []).map((svc, idx) => {
              const key = svc.id || idx;
              const img = svc.image || '/static/images/default_image.png';
              const text = svc.description || '';
              const tooLong = text.length > 200;
              const subCategories = svc.subCategories || [];
              const expanded = !!expandedServices[key];
              const shown =
                !tooLong || expanded ? text : `${text.slice(0, 200)}...`;

              return (
                <div key={key} className={css.serviceCardItem}>
                  <div className={css.serviceImageWrapper}>
                    <img
                      src={img}
                      alt={svc.label || 'Service'}
                      className={css.serviceImage}
                      loading="lazy"
                    />
                  </div>
                  <div className={css.serviceContent}>
                    {svc.label ? (
                      <h3 className={css.serviceTitle}>{svc.label}</h3>
                    ) : null}
                    {Array.isArray(subCategories) && subCategories.length > 0 ? 
                      <ul className={css.subCategoryServices}>
                        {subCategories.map(item =>{
                          return (
                            <li>{getSubCategoryLabel(categories, item)}</li>
                          )
                        })}
                      </ul>
                    : null}
                    {text ? (
                      <p className={css.serviceDescription}>
                        {shown}
                        {tooLong ? (
                          <button
                            type="button"
                            className={css.serviceReadMore}
                            onClick={() => toggleServiceDesc(key)}
                          >
                            {expanded ? ' Read less' : ' Read more'}
                          </button>
                        ) : null}
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p>No services have been added to this profile yet.</p>
        )}
      </section>

      {/* Gallery */}
      <section
        id="gallery"
        ref={el => {
          sectionRefs.current.gallery = el;
        }}
        className={classNames(css.section, css.gallerySection)}
      >
        <h2 className={css.sectionTitle}>Gallery</h2>
        {hasGallery ? (
          <Slider {...settings}>
            {companyGalleryPhotos.map((src, index) => (
              <div key={index} className={css.slide}>
                <img
                  src={src}
                  alt={`Gallery image ${index + 1}`}
                  className={css.image}
                  loading="lazy"
                />
              </div>
            ))}
          </Slider>
        ) : (
          <p>No gallery images have been added to this profile yet.</p>
        )}
      </section>

      {/* Reviews */}
      {props.reviews && props.reviews.length > 0 ? (
        <section
          id="reviews"
          ref={el => {
            sectionRefs.current.reviews = el;
          }}
          className={classNames(css.section, css.reviewsSection)}
        >
          <h2 className={css.sectionTitle}>Reviews</h2>
          {isMobileLayout ? (
            <MobileReviews
              reviews={props.reviews || []}
              queryReviewsError={props.queryReviewsError}
            />
          ) : (
            <DesktopReviews
              reviews={props.reviews || []}
              queryReviewsError={props.queryReviewsError}
            />
          )}
        </section>
      ) : null}

      {/* Location Details */}
      <section
        id="location"
        ref={el => {
          sectionRefs.current.location = el;
        }}
        className={classNames(css.section, css.locationSection)}
      >
        <div className={css.locationCard}>
          <div className={css.locationHeaderRow}>
            <h2 className={css.locationTitle}>Location Details</h2>
            <div className={css.locationHeaderRowContent}>
              {addressLine ? (
                <div className={css.locationAddress}>{addressLine}</div>
              ) : null}
              {addressLine ? (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    addressLine
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.locationGoogleLink}
                >
                  View on Google
                </a>
              ) : null}
            </div>
          </div>
          {hasLocation ? (
            <>
              {geolocation ? (
                <div className={css.locationMapWrapper}>
                  <SectionMapMaybe
                    className={css.locationMapInner}
                    rootClassName={css.locationMapInner}
                    geolocation={geolocation}
                    publicData={{ location: { address: addressLine } }}
                    listingId={profileUser.id}
                    mapsConfig={mapsConfig}
                  />
                </div>
              ) : null}
            </>
          ) : (
            <p>Location details have not been provided yet.</p>
          )}
        </div>
      </section>

      {/* FAQs */}
      <section
        id="faqs"
        ref={el => {
          sectionRefs.current.faqs = el;
        }}
        className={classNames(css.section, css.faqsSection)}
      >
        <h2 className={css.sectionTitle}>FAQs</h2>
        {hasFaqs ? (
          <div className={css.faqList}>
            {faqsList.map((item, idx) => {
              const id = idx + 1;
              const isOpen = !!openFaqs[id];
              const title = item.label || `Question ${id}`;
              const body = item.answer || '';
              return (
                <div
                  key={id}
                  className={classNames(css.faqItem, {
                    [css.faqItemOpen]: isOpen,
                  })}
                >
                  <button
                    type="button"
                    className={css.faqHeader}
                    onClick={() =>
                      setOpenFaqs(prev => ({ ...prev, [id]: !prev[id] }))
                    }
                  >
                    <span className={css.faqTitle}>{title}</span>
                    <span className={css.faqToggle} aria-hidden="true">
                      {isOpen ? '×' : '+'}
                    </span>
                  </button>
                  {isOpen ? <div className={css.faqBody}>{body}</div> : null}
                </div>
              );
            })}
          </div>
        ) : (
          <p>No FAQs have been added to this profile yet.</p>
        )}
      </section>
    </div>
  );
};

export default CompanyProfilePage;
